import React, { useContext, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { wrap } from 'popmotion';
import TagManager from 'react-gtm-module';

import style from './interactive-track.module.scss';
import {
  CosmosButton,
  CosmosIconArrowLeft,
  CosmosIconArrowRight,
  CosmosText
} from '../../cosmos/web-scoped/react';
import { Marker } from './marker';
import { OffCanvas } from '../OffCanvas';
import { hashCode } from '../../Utils/string';
import { trigger } from '../../Utils/events';
import { AppContext } from '../../App';
import { getTrans } from '../../Utils/translate';

const tagChange = (athlete, direction, title) => {
  const dataLayerArgs = {
    dataLayer: {
      event: 'rbVideoTracking',
      eventCategory: 'video',
      eventAction: 'sidebar',
      eventLabel: `Slider – ${direction}`,
      widgetType: `InteractiveTrack - ${title}`,
      widgetTypeProfile: `${athlete.label}`
    }
  };

  TagManager.dataLayer(dataLayerArgs);
};

const Image = ({ setAutopoll, alt, options, selected, setSelected, config, textboxes }) => {
  const [[page, direction], setPage] = useState([0, 0]);
  const [pageInitiated, setPageInitiated] = useState(false);
  const context = useContext(AppContext);
  const { moreDetails } = context.lang;
  const { backToOverview } = context.lang;
  const [topOffset, setTopOffset] = useState(400);

  const variants = {
    enter: (direction) => {
      return {
        x: direction > 0 ? 1000 : -1000,
        opacity: 0
      };
    },
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction) => {
      return {
        zIndex: 0,
        x: direction < 0 ? 1000 : -1000,
        opacity: 0
      };
    }
  };
  const animationConfig = context.config.animation;
  const containerAnimation = {
    hidden: {
      opacity: 1,
      scale: 0,
      transition: {
        duration: animationConfig.duration,
        ease: animationConfig.ease
      }
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        when: 'beforeChildren',
        staggerChildren: 0.1,
        duration: animationConfig.duration,
        ease: animationConfig.ease
      }
    }
  };
  const itemAnimation = {
    hidden: {
      y: 20,
      opacity: 0,
      transition: {
        duration: animationConfig.duration,
        ease: animationConfig.ease
      }
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: animationConfig.duration,
        ease: animationConfig.ease
      }
    }
  };

  const calculateScroll = (e) => {
    if (e) {
      const [scrollTop, scrollLeft] = calculateScroll(e.parentNode);
      return [(e.scrollTop || 0) + scrollTop, (e.scrollLeft || 0) + scrollLeft];
    }else {
      return [0, 0];
    }
  };

  useEffect(() => {
    const currentIndex = options.findIndex((option) => option.value === selected.value);
    let newDirection = -1;

    if (
      (currentIndex > page && currentIndex !== options.length - 1) ||
      (currentIndex === options.length - 1 && page === options.length - 2) ||
      (page === options.length - 1 && currentIndex === 0)
    ) {
      newDirection = 1;
    }

    selected.textboxes.map((textbox) => {
      // cleanup crosslinks
      const slide = options.find((option) => (option.label === textbox.crosslink));
      if (!slide) {
        delete textbox.crosslink;
      }
      return slide;
    });

    setPage([currentIndex, newDirection]);

    if (!pageInitiated) {
      setPageInitiated(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  return (
    <div style={{ minHeight: config.height, position: 'relative', marginTop: -16 }}>
      <AnimatePresence custom={direction}>
        <motion.div
          key={options[page].label}
          style={{ position: 'absolute', left: 0, width: '100%' }}
          custom={direction}
          initial={pageInitiated ? `enter` : false}
          animate="center"
          variants={variants}
          transition={{
            x: { type: 'spring', stiffness: 300, damping: 30 },
            opacity: { duration: 0.2 }
          }}
          exit="exit">
          <div style={{ position: 'relative' }}>
            {textboxes && textboxes.map((textbox, index) => (
                <div key={index}>
                  <AnimatePresence>
                  {textbox.bullet && textbox.bullet.name && (
                    <motion.div
                      style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%'
                      }}
                      initial={{ opacity: 0, top: '-100px' }}
                      animate={{ opacity: 1, top: '0' }}
                      transition={{
                        type: "spring",
                        stiffness: 100,
                        delay: index++ / 2
                      }}>
                      <Marker
                        label={getTrans(textbox.bullet.name) || textbox.bullet.name}
                        classNames={style.marker}
                        position={{
                          bottom: textbox.bullet.bottom,
                          left: textbox.bullet.left
                        }}
                        placement={textbox.bullet.placement || 'above'}
                        onClick={(e) => {
                          // set off-canvas bounds based relative  marker position
                          const element = e.currentTarget;
                          const [scrollTop] = calculateScroll(element);
                          const markerRect = element.getBoundingClientRect();
                          setTopOffset(markerRect.top + scrollTop + 44);
                          trigger(`offCanvasOpen-${hashCode(textbox)}`, { group: hashCode(selected.label) });
                        }} />
                      </motion.div>
                  )}
                  </AnimatePresence>
                </div>
              )
            )}
            <img
              style={{ width: '100%' }}
              alt={alt}
              src={options[page].image}
              className="image"
            />
          </div>
          {/* content under image */}
          {(config.hasOwnProperty('showBackCrosslink') ? config.showBackCrosslink : true) && page > 0 && (
            <CosmosButton
              kind="secondary"
              onClick={() => setSelected(options[0])}>
              {getTrans(backToOverview)}
            </CosmosButton>
          )}
          <motion.div
            variants={containerAnimation}
            initial="hidden"
            animate="visible">
            {textboxes && textboxes.map((textbox, index) => (
              <motion.div
                key={index}
                style={{ textAlign: 'left', margin: '16px' }}
                variants={itemAnimation}>
                {!textbox.bullet && (
                  <p>
                    <CosmosText weight="bold">{getTrans(textbox.headline)}</CosmosText>
                    <CosmosText kind="subtle">{getTrans(textbox.text)}</CosmosText>
                  </p>
                )}
            </motion.div>
          ))}
          </motion.div>
          {options[page].buttons &&
            Array.isArray(options[page].buttons) &&
            options[page].buttons.length > 0 &&
            options[page].buttons.map((button, index) => {
              const buttonVariants = {
                ...variants,
                center: {
                  ...variants.center,
                  x:
                    button.position.left === '50%'
                      ? '-50%'
                      : button.position.right === '50%'
                        ? '50%'
                        : 0
                }
              };

              return (
                <motion.div
                  key={`${index}-${button.text}-${page}`}
                  style={{ ...button.position, position: 'absolute', zIndex: 1000 }}
                  custom={direction}
                  variants={buttonVariants}
                  transition={{
                    x: { type: 'spring', stiffness: 300, damping: 30 },
                    opacity: { duration: 0.2 }
                  }}
                  exit="exit"
                  className="image"
                  initial="enter"
                  animate="center">
                  <CosmosButton
                    key={`${index}-${button.text}-${page}`}
                    href={button.link}
                    kind="secondary"
                    rel="external"
                    target="_blank"
                    onClick={() => {
                      const dataLayerArgs = {
                        dataLayer: {
                          event: 'rbVideoTracking',
                          eventCategory: 'video',
                          eventAction: 'sidebar',
                          eventLabel: `Button - ${button.text}`,
                          widgetType: `InteractiveTrack - ${config.trackingTitle || config.navigationTitle}`,
                          widgetTypeProfile: `${options[page].label}`
                        }
                      };
                      TagManager.dataLayer(dataLayerArgs);
                    }}>
                    {getTrans(button.text)}
                  </CosmosButton>
                </motion.div>
              );
            })}
        </motion.div>
      </AnimatePresence>
      <CosmosButton
        class={`${style.controlls}`}
        style={{ top: config.arrowPostionFromTop }}
        onClick={() => {
          setAutopoll(false);
          const nextIndex = wrap(0, options.length, page - 1);
          tagChange(
            options[nextIndex],
            'Previous',
            config.trackingTitle || config.navigationTitle
          );
          setSelected(options[nextIndex]);
        }}
        kind="bare"
        shape="circle">
        <CosmosIconArrowLeft slot="icon" style={{ width: '32px', height: '32px' }} />
      </CosmosButton>
      <CosmosButton
        class={`${style.controlls}  ${style.right}`}
        style={{ top: config.arrowPostionFromTop }}
        onClick={() => {
          setAutopoll(false);
          const nextIndex = wrap(0, options.length, page + 1);
          tagChange(
            options[nextIndex],
            'Next',
            config.trackingTitle || config.navigationTitle
          );
          setSelected(options[nextIndex]);
        }}
        kind="bare"
        shape="circle">
        <CosmosIconArrowRight slot="icon" style={{ width: '32px', height: '32px' }} />
      </CosmosButton>
      {textboxes && textboxes.map((textbox, index) => (
        <div key={index}>
          <OffCanvas
            id={hashCode(textbox)}
            group={hashCode(selected.label)}
            topOffset={topOffset}>
            <div className="textbox-body">
              {textbox.bullet &&
                <div className="marker-indicator marker-indicator-small">
                  <CosmosText weight="bold" appearance="light">{getTrans(textbox.bullet.name) || textbox.bullet.name}</CosmosText>
                </div>
              }
              <div>
                <CosmosText weight="bold">{getTrans(textbox.headline)}</CosmosText>
                <CosmosText>{getTrans(textbox.text)}</CosmosText>
              </div>
            </div>
            {textbox.crosslink &&
              <div className="textbox-footer">
                <CosmosButton
                  shape="pill"
                  onClick={() => {
                    const slide = options.find((option) => (option.label === textbox.crosslink));
                    if (slide) {
                      setSelected(slide);
                    } else {
                      textbox.crosslink = false;
                    }
                  }}>
                  {getTrans(moreDetails)}
                </CosmosButton>
              </div>
            }
          </OffCanvas>
        </div>
      ))};
    </div>
  );
};

export { Image };
